import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  DollarSign, 
  TrendingUp, 
  Users, 
  Briefcase,
  PieChart,
  Clock,
  Save,
  Plus,
  Trash2,
  Coffee,
  Calendar,
  BookOpen,
  Users2,
  Plane,
  Monitor,
  MoreHorizontal,
  UserPlus,
  UserMinus,
  Shield,
  Edit2,
  X,
  ChevronRight,
  CheckCircle2,
  BarChart3,
  PoundSterling,
  BotMessageSquare,
  Lock,
  Mail,
  AlertTriangle,
  EyeOff,
  RefreshCw
} from "lucide-react";
import MetricInfo from "../components/MetricInfo";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

import { 
  PieChart as RechartsPie, Pie, Cell, BarChart, Bar, XAxis, YAxis, 
  CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from "recharts";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import DataTable from "../components/DataTable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  admin: Monitor, lunch: Coffee, training: BookOpen, meetings: Users2,
  leave: Calendar, travel: Plane, internal: Users, it_support: Monitor, other: MoreHorizontal
};

const CATEGORY_COLORS = {
  admin: "#6366F1", lunch: "#F59E0B", training: "#10B981", meetings: "#EC4899",
  leave: "#8B5CF6", travel: "#06B6D4", internal: "#F97316", it_support: "#84CC16", other: "#94A3B8"
};

// ============== SESSION MANAGEMENT PANEL ==============
function SessionManagementPanel() {
  const [activeSessions, setActiveSessions] = useState([]);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyDays, setHistoryDays] = useState(7);

  useEffect(() => { fetchSessions(); }, [historyDays]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const [activeRes, histRes] = await Promise.all([
        axios.get(`${API}/admin/sessions/active`),
        axios.get(`${API}/admin/sessions/history?days=${historyDays}&limit=50`),
      ]);
      setActiveSessions(activeRes.data?.sessions || []);
      setSessionHistory(histRes.data?.sessions || []);
    } catch (err) {
      toast.error("Failed to load session data");
    } finally {
      setLoading(false);
    }
  };

  const handleForceLogout = async (userId, userName) => {
    if (!window.confirm(`Force logout ${userName || "this user"}? Their session will be terminated immediately.`)) return;
    try {
      await axios.post(`${API}/admin/sessions/force-logout`, { user_id: userId });
      toast.success(`${userName || "User"} has been forcefully logged out`);
      fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to force logout");
    }
  };

  const formatTime = (ts) => {
    if (!ts) return "N/A";
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="sessions-panel">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Active Sessions</CardTitle>
              <CardDescription>{activeSessions.length} active session(s)</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchSessions} data-testid="refresh-sessions-btn">
              <RefreshCw className="w-4 h-4 mr-2" />Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {activeSessions.length === 0 ? (
            <p className="text-muted-foreground text-sm">No active sessions</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium">User</th>
                    <th className="pb-2 font-medium">Role</th>
                    <th className="pb-2 font-medium">IP</th>
                    <th className="pb-2 font-medium">Device</th>
                    <th className="pb-2 font-medium">Location</th>
                    <th className="pb-2 font-medium">Login Time</th>
                    <th className="pb-2 font-medium">Last Activity</th>
                    <th className="pb-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activeSessions.map((s, i) => (
                    <tr key={s.session_id || i} className="border-b border-border/50 hover:bg-muted/30">
                      <td className="py-2">
                        <div className="font-medium">{s.user_name || "Unknown"}</div>
                        <div className="text-xs text-muted-foreground">{s.user_email}</div>
                      </td>
                      <td className="py-2"><Badge variant="outline">{s.user_role}</Badge></td>
                      <td className="py-2 font-mono text-xs">{s.ip}</td>
                      <td className="py-2 text-xs">{s.browser} / {s.os}</td>
                      <td className="py-2 text-xs">{s.city ? `${s.city}, ${s.country}` : s.country || "Unknown"}</td>
                      <td className="py-2 text-xs">{formatTime(s.login_time)}</td>
                      <td className="py-2 text-xs">{formatTime(s.last_activity)}</td>
                      <td className="py-2">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleForceLogout(s.user_id, s.user_name)}
                          data-testid={`force-logout-${s.user_id}`}
                        >
                          Force Logout
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Session History</CardTitle>
              <CardDescription>Last {historyDays} days ({sessionHistory.length} entries)</CardDescription>
            </div>
            <Select value={String(historyDays)} onValueChange={(v) => setHistoryDays(Number(v))}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 day</SelectItem>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {sessionHistory.length === 0 ? (
            <p className="text-muted-foreground text-sm">No session history</p>
          ) : (
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card">
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium">User</th>
                    <th className="pb-2 font-medium">Status</th>
                    <th className="pb-2 font-medium">IP</th>
                    <th className="pb-2 font-medium">Login</th>
                    <th className="pb-2 font-medium">Ended</th>
                    <th className="pb-2 font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {sessionHistory.map((s, i) => (
                    <tr key={s.id || i} className="border-b border-border/50">
                      <td className="py-1.5">
                        <div className="font-medium text-xs">{s.user_name || s.user_email || "?"}</div>
                      </td>
                      <td className="py-1.5">
                        <Badge variant={s.active ? "default" : "secondary"} className="text-xs">
                          {s.active ? "Active" : "Ended"}
                        </Badge>
                      </td>
                      <td className="py-1.5 font-mono text-xs">{s.ip}</td>
                      <td className="py-1.5 text-xs">{formatTime(s.login_time)}</td>
                      <td className="py-1.5 text-xs">{s.ended_at ? formatTime(s.ended_at) : "-"}</td>
                      <td className="py-1.5 text-xs text-muted-foreground">{s.ended_reason || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============== AUDIT TRAIL PANEL ==============
function AuditTrailPanel() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [filterCollection, setFilterCollection] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [total, setTotal] = useState(0);

  useEffect(() => { fetchAuditTrail(); }, [days, filterCollection, filterEmail]);

  const fetchAuditTrail = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days), limit: "100" });
      if (filterCollection) params.set("collection", filterCollection);
      if (filterEmail) params.set("user_email", filterEmail);
      const res = await axios.get(`${API}/admin/audit-trail?${params.toString()}`);
      setLogs(res.data?.logs || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      toast.error("Failed to load audit trail");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const token = sessionStorage.getItem("auth_token");
    window.open(`${API}/admin/audit-trail/export?days=${days}&token=${token}`, "_blank");
  };

  const formatTime = (ts) => {
    if (!ts) return "N/A";
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="audit-trail-panel">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-lg">Field-Level Audit Trail</CardTitle>
              <CardDescription>Tracks all changes to users, projects, and settings ({total} total)</CardDescription>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Filter by email..."
                value={filterEmail}
                onChange={(e) => setFilterEmail(e.target.value)}
                className="w-40"
                data-testid="audit-filter-email"
              />
              <Select value={filterCollection || "all"} onValueChange={(v) => setFilterCollection(v === "all" ? "" : v)}>
                <SelectTrigger className="w-32"><SelectValue placeholder="Collection" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="users">Users</SelectItem>
                  <SelectItem value="config">Config</SelectItem>
                  <SelectItem value="projects_master">Projects</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={handleExport} data-testid="audit-export-btn">
                Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-muted-foreground text-sm">No audit entries found for the selected period.</p>
          ) : (
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card">
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium">Timestamp</th>
                    <th className="pb-2 font-medium">User</th>
                    <th className="pb-2 font-medium">Action</th>
                    <th className="pb-2 font-medium">Collection</th>
                    <th className="pb-2 font-medium">Changes</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => (
                    <tr key={log.id || i} className="border-b border-border/50 hover:bg-muted/30">
                      <td className="py-2 text-xs whitespace-nowrap">{formatTime(log.timestamp)}</td>
                      <td className="py-2 text-xs">{log.user_email || "System"}</td>
                      <td className="py-2">
                        <Badge variant="outline" className="text-xs">{log.action}</Badge>
                      </td>
                      <td className="py-2 text-xs font-mono">{log.collection}</td>
                      <td className="py-2 text-xs max-w-xs">
                        {log.field_changes && Object.keys(log.field_changes).length > 0 ? (
                          <div className="space-y-0.5">
                            {Object.entries(log.field_changes).slice(0, 3).map(([field, change]) => (
                              <div key={field} className="flex gap-1 items-center">
                                <span className="font-medium text-foreground">{field}:</span>
                                <span className="text-destructive/80 line-through truncate max-w-[80px]">{String(change?.old ?? "null")}</span>
                                <span className="text-muted-foreground">→</span>
                                <span className="text-emerald-600 dark:text-emerald-400 truncate max-w-[80px]">{String(change?.new ?? "null")}</span>
                              </div>
                            ))}
                            {Object.keys(log.field_changes).length > 3 && (
                              <span className="text-muted-foreground">+{Object.keys(log.field_changes).length - 3} more</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============== TEAM LEADERS PANEL ==============
function TeamLeadersPanel() {
  const [assignments, setAssignments] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [unassignedUsers, setUnassignedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeader, setSelectedLeader] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [editingLeaderId, setEditingLeaderId] = useState(null);
  const [memberSearch, setMemberSearch] = useState("");
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [pendingPayload, setPendingPayload] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [assignRes, usersRes, unassignedRes] = await Promise.all([
        axios.get(`${API}/admin/team-assignments`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/team-assignments/unassigned-users`),
      ]);
      setAssignments(assignRes.data?.assignments || []);
      const usersList = usersRes.data?.items || usersRes.data || [];
      setAllUsers(usersList);
      setUnassignedUsers(unassignedRes.data?.users || []);
    } catch (err) {
      toast.error("Failed to load team assignments");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (force = false) => {
    if (!selectedLeader) { toast.error("Select a team leader"); return; }
    try {
      const payload = { team_leader_id: selectedLeader, member_ids: selectedMembers, force };
      const res = await axios.post(`${API}/admin/team-assignments`, payload);
      if (res.status === 409) {
        setConflicts(res.data?.conflicts || []);
        setPendingPayload(payload);
        setShowConflictDialog(true);
        return;
      }
      toast.success(res.data?.message || "Team assignment saved");
      resetForm();
      fetchData();
    } catch (err) {
      if (err.response?.status === 409) {
        setConflicts(err.response.data?.conflicts || []);
        setPendingPayload({ team_leader_id: selectedLeader, member_ids: selectedMembers, force: true });
        setShowConflictDialog(true);
      } else {
        toast.error(err.response?.data?.detail || "Failed to save team assignment");
      }
    }
  };

  const handleForceReassign = async () => {
    if (!pendingPayload) return;
    try {
      const res = await axios.post(`${API}/admin/team-assignments`, pendingPayload);
      toast.success(res.data?.message || "Team reassigned");
      setShowConflictDialog(false);
      setConflicts([]);
      setPendingPayload(null);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reassign");
    }
  };

  const handleDelete = async (leaderId, leaderName) => {
    if (!window.confirm(`Remove team assignment for ${leaderName}? All team members will become unassigned.`)) return;
    try {
      await axios.delete(`${API}/admin/team-assignments/${leaderId}`);
      toast.success("Team assignment removed");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove");
    }
  };

  const handleEdit = (assignment) => {
    setEditingLeaderId(assignment.team_leader_id);
    setSelectedLeader(assignment.team_leader_id);
    setSelectedMembers(assignment.member_ids || []);
  };

  const resetForm = () => {
    setSelectedLeader("");
    setSelectedMembers([]);
    setEditingLeaderId(null);
    setMemberSearch("");
  };

  const toggleMember = (userId) => {
    setSelectedMembers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const availableMembers = allUsers.filter(u => 
    u.id !== selectedLeader && 
    u.name?.toLowerCase().includes(memberSearch.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="team-leaders-panel">
      {/* Create/Edit Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{editingLeaderId ? "Edit Team Assignment" : "Assign Team Leader"}</CardTitle>
          <CardDescription>Select a user to designate as Team Leader, then assign their team members.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Team Leader</Label>
              <Select value={selectedLeader} onValueChange={(v) => { setSelectedLeader(v); setSelectedMembers([]); }} disabled={!!editingLeaderId}>
                <SelectTrigger data-testid="team-leader-select"><SelectValue placeholder="Select a user..." /></SelectTrigger>
                <SelectContent>
                  {allUsers.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} ({u.role}) — {u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Search Members</Label>
              <Input 
                placeholder="Filter users..." 
                value={memberSearch} 
                onChange={(e) => setMemberSearch(e.target.value)}
                data-testid="team-member-search"
              />
            </div>
          </div>

          {selectedLeader && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Team Members ({selectedMembers.length} selected)</Label>
              <div className="border rounded-lg max-h-48 overflow-y-auto p-2 space-y-1">
                {availableMembers.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-2">No users found</p>
                ) : (
                  availableMembers.map(u => (
                    <label key={u.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedMembers.includes(u.id)}
                        onChange={() => toggleMember(u.id)}
                        className="rounded border-border"
                      />
                      <span className="text-sm">{u.name}</span>
                      <Badge variant="outline" className="text-xs ml-auto">{u.role}</Badge>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button onClick={() => handleSave(false)} disabled={!selectedLeader} data-testid="save-team-btn">
              <Save className="w-4 h-4 mr-2" />{editingLeaderId ? "Update Team" : "Create Team"}
            </Button>
            {editingLeaderId && (
              <Button variant="outline" onClick={resetForm}>Cancel</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Existing Assignments */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Current Team Assignments</CardTitle>
              <CardDescription>{assignments.length} team leader(s) configured</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
          </div>
        </CardHeader>
        <CardContent>
          {assignments.length === 0 ? (
            <p className="text-muted-foreground text-sm">No team assignments yet. Create one above.</p>
          ) : (
            <div className="space-y-4">
              {assignments.map((a) => (
                <div key={a.team_leader_id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users2 className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{a.leader_name}</p>
                        <span className="text-xs text-muted-foreground">{a.leader_email} · <Badge variant="outline" className="text-xs">{a.leader_role}</Badge></span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(a)} data-testid={`edit-team-${a.team_leader_id}`}>
                        <Edit2 className="w-3.5 h-3.5 mr-1" />Edit
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => handleDelete(a.team_leader_id, a.leader_name)} data-testid={`delete-team-${a.team_leader_id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" />Remove
                      </Button>
                    </div>
                  </div>
                  <div className="pl-13">
                    <p className="text-xs font-medium text-muted-foreground mb-1">{a.members?.length || 0} Team Member(s):</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(a.members || []).map(m => (
                        <Badge key={m.id} variant="secondary" className="text-xs">{m.name} ({m.role})</Badge>
                      ))}
                      {(!a.members || a.members.length === 0) && <span className="text-xs text-muted-foreground">No members assigned</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unassigned Users */}
      {unassignedUsers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Unassigned Users</CardTitle>
            <CardDescription>{unassignedUsers.length} user(s) not in any team</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {unassignedUsers.map(u => (
                <Badge key={u.id} variant="outline" className="text-xs py-1">{u.name} ({u.role})</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Conflict Resolution Dialog */}
      {showConflictDialog && (
        <Dialog open={showConflictDialog} onOpenChange={setShowConflictDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Member Conflict</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">The following members are already assigned to other team leaders:</p>
              {conflicts.map((c, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                  <span className="text-sm font-medium">{c.member_name}</span>
                  <span className="text-xs text-muted-foreground">Currently with: {c.current_leader}</span>
                </div>
              ))}
              <p className="text-sm">Would you like to reassign them to the new team leader?</p>
            </div>
            <DialogFooter>
              <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
              <Button onClick={handleForceReassign} data-testid="force-reassign-btn">Reassign</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}




export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("system");
  const [profitability, setProfitability] = useState(null);
  const [nonFeeAnalysis, setNonFeeAnalysis] = useState(null);
  const [projectBudgets, setProjectBudgets] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectSearch, setProjectSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");

  // System health state
  const [systemHealth, setSystemHealth] = useState(null);
  const [systemSecurity, setSystemSecurity] = useState(null);
  const [auditLog, setAuditLog] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectAnalysis, setProjectAnalysis] = useState(null);

  // AI settings & state
  const [aiSettings, setAiSettings] = useState({ ai_enabled: true, default_provider: "openai" });
  const [aiProvider, setAiProvider] = useState("");
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiAnswer, setAiAnswer] = useState("");

  const [filterOptions, setFilterOptions] = useState({ consultants: [], projects: [] });
  const [selectedConsultants, setSelectedConsultants] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);

  // Date range (inclusive) for admin analytics
  const [dateRange, setDateRange] = useState({ start: "", end: "" });

  // Visibility defaults state
  const [visDefaults, setVisDefaults] = useState({ excluded_personnel: [], excluded_project_ids: [], all_personnel: [], all_project_ids: [] });
  const [visPersonnelSearch, setVisPersonnelSearch] = useState("");
  const [visProjectSearch, setVisProjectSearch] = useState("");
  const [visSaving, setVisSaving] = useState(false);

  const [aiLoading, setAiLoading] = useState(false);
  
  // Project assignments state
  const [assignments, setAssignments] = useState([]);
  const [assignFormOpen, setAssignFormOpen] = useState(false);
  const [assignManagerId, setAssignManagerId] = useState("");
  const [assignProjectIds, setAssignProjectIds] = useState([]);
  const [assignSearch, setAssignSearch] = useState("");
  const [assignProjectHours, setAssignProjectHours] = useState({}); // {pid: {prep: "", delivery: ""}}
  const [masterQuotedHours, setMasterQuotedHours] = useState({}); // {pid: {prep_quoted, delivery_quoted, total_quoted}}

  // Fetch master quoted hours when projects are selected
  const fetchMasterHours = async (pids) => {
    if (pids.length === 0) { setMasterQuotedHours({}); return; }
    try {
      const res = await axios.post(`${API}/admin/projects-master/lookup`, { project_ids: pids });
      setMasterQuotedHours(res.data.projects || {});
    } catch {}
  };
  
  // Form states
  const [newBudget, setNewBudget] = useState({ project_id: "", budget_hours: "", budget_amount: "", hourly_rate: "", assigned_personnel: [] });

  // Auto-fill budget £ from Project Master (via profitability.by_project) when selecting a project
  useEffect(() => {
    if (!newBudget.project_id) return;
    if (newBudget.budget_amount) return;
    const master = profitability?.by_project?.find((p) => p.project_id === newBudget.project_id);
    if (master?.project_value_gbp) {
      setNewBudget((prev) => ({ ...prev, budget_amount: String(master.project_value_gbp) }));
    }
  }, [newBudget.project_id, profitability]);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "employee" });
  const [editingUser, setEditingUser] = useState(null);
  const [securityFeatures, setSecurityFeatures] = useState([]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConsultants, selectedProjects, dateRange.start, dateRange.end]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (dateRange.start) qs.set("start_date", dateRange.start);
      if (dateRange.end) qs.set("end_date", dateRange.end);
      if (selectedConsultants.length > 0) qs.set("consultants", selectedConsultants.join(","));
      if (selectedProjects.length > 0) qs.set("projects", selectedProjects.join(","));
      const params = qs.toString() ? `?${qs.toString()}` : "";

      const [profitRes, nonFeeRes, budgetsRes, consultantsRes, projectsRes, usersRes, aiSettingsRes, filterRes, assignRes] = await Promise.all([
        axios.get(`${API}/admin/profitability${params}`),
        axios.get(`${API}/admin/non-fee-analysis${params}`),
        axios.get(`${API}/admin/project-budgets`),
        axios.get(`${API}/consultants${params}`),
        axios.get(`${API}/admin/projects${params}`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/settings/ai`),
        axios.get(`${API}/filter-options`),
        axios.get(`${API}/admin/project-assignments`).catch(() => ({ data: { assignments: [] } })),
      ]);

      const opts = filterRes.data || { consultants: [], projects: [] };
      setFilterOptions(opts);
      if (selectedConsultants.length === 0 && opts.consultants?.length) {
        setSelectedConsultants(opts.consultants);
      }
      if (selectedProjects.length === 0 && opts.projects?.length) {
        setSelectedProjects(opts.projects);
      }

      setProfitability(profitRes.data);
      setNonFeeAnalysis(nonFeeRes.data);
      setProjectBudgets(budgetsRes.data?.items || budgetsRes.data || []);
      setConsultants(consultantsRes.data);
      setProjects(projectsRes.data?.items || projectsRes.data || []);
      setUsers(usersRes.data?.items || usersRes.data || []);
      setAiSettings(aiSettingsRes.data);
      setAiProvider(aiSettingsRes.data.default_provider || "openai");
      setAssignments(assignRes.data.assignments || []);

      // Fetch system data
      try {
        const [healthRes, secRes, auditRes] = await Promise.all([
          axios.get(`${API}/admin/system/health`),
          axios.get(`${API}/admin/system/security`),
          axios.get(`${API}/admin/system/audit-log`),
        ]);
        setSystemHealth(healthRes.data);
        setSystemSecurity(secRes.data);
        setAuditLog(auditRes.data);
      } catch {}

      // Fetch security features real-time status
      try {
        const featRes = await axios.get(`${API}/admin/system/security-features`);
        setSecurityFeatures(featRes.data?.features || []);
      } catch {}

      // Fetch visibility defaults
      try {
        const visRes = await axios.get(`${API}/admin/visibility-defaults`);
        setVisDefaults(visRes.data);
      } catch {}
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectAnalysis = async (projectId) => {
    try {
      const response = await axios.get(`${API}/admin/project-analysis/${encodeURIComponent(projectId)}`);
      setProjectAnalysis(response.data);
      setSelectedProject(projectId);
    } catch (error) {
      toast.error("Failed to load project analysis");
    }
  };

  const togglePersonnelExclusion = (name) => {
    setVisDefaults((prev) => {
      const set = new Set(prev.excluded_personnel || []);
      if (set.has(name)) set.delete(name); else set.add(name);
      return { ...prev, excluded_personnel: Array.from(set) };
    });
  };

  const toggleProjectExclusion = (pid) => {
    setVisDefaults((prev) => {
      const set = new Set(prev.excluded_project_ids || []);
      if (set.has(pid)) set.delete(pid); else set.add(pid);
      return { ...prev, excluded_project_ids: Array.from(set) };
    });
  };

  const handleSaveVisibilityDefaults = async () => {
    setVisSaving(true);
    try {
      await axios.put(`${API}/admin/visibility-defaults`, {
        excluded_personnel: visDefaults.excluded_personnel || [],
        excluded_project_ids: visDefaults.excluded_project_ids || [],
      });
      toast.success("Visibility defaults saved");
      fetchData();
    } catch (e) {
      toast.error("Failed to save visibility defaults");
    } finally {
      setVisSaving(false);
    }
  };

  const handleSaveBudget = async () => {
    if (!newBudget.project_id || !newBudget.budget_hours) {
      toast.error("Please select a project and enter budget hours");
      return;
    }
    
    const project = projects.find(p => p.project_id === newBudget.project_id);
    const master = profitability?.by_project?.find(p => p.project_id === newBudget.project_id);
    
    try {
      await axios.post(`${API}/admin/project-budgets`, {
        project_id: newBudget.project_id,
        project_name: master?.project_name || project?.company || newBudget.project_id,
        company: master?.client_name || project?.company || "",
        budget_hours: parseFloat(newBudget.budget_hours),
        // If user didn't enter budget_amount, auto-fill from master Project Value (master-first)
        budget_amount: parseFloat(newBudget.budget_amount || master?.project_value_gbp || master?.revenue || 0),
        hourly_rate: parseFloat(newBudget.hourly_rate || 0),
        assigned_personnel: newBudget.assigned_personnel,
        is_billable: true,
        currency: "GBP"
      });
      toast.success("Budget saved successfully");
      setNewBudget({ project_id: "", budget_hours: "", budget_amount: "", hourly_rate: "", assigned_personnel: [] });
      fetchData();
    } catch (error) {
      toast.error("Failed to save budget");
    }
  };

  const handleDeleteBudget = async (projectId) => {
    try {
      await axios.delete(`${API}/admin/project-budgets/${encodeURIComponent(projectId)}`);
      toast.success("Budget deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete budget");
    }
  };

  // Invite details after user creation
  const [inviteDetails, setInviteDetails] = useState(null);

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.name) {
      toast.error("Please fill in name and email");
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/users`, {
        ...newUser,
        password: newUser.password || `Temp${Date.now()}!Aa`,
      });
      setInviteDetails({
        name: newUser.name,
        email: newUser.email,
        role: newUser.role,
        emailSent: res.data.email_sent,
      });
      setNewUser({ email: "", password: "", name: "", role: "employee" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, updates);
      toast.success("User updated");
      setEditingUser(null);
      // stay on Users tab
      setActiveTab("users");
      fetchData();
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleDeleteUser = async (userId) => {
    // NOTE: window.confirm is blocked in the preview sandbox (no allow-modals),
    // so we do not use confirm() here. Delete happens immediately on click.
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      // Optimistically update UI
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      setSelectedUserIds((prev) => prev.filter((id) => id !== userId));
      toast.success("User deleted");
      // stay on Users tab
      setActiveTab("users");
      // Also refresh from server in background to stay in sync
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleChartClick = (data, type) => {
    if (type === "consultant" && data?.personnel) {
      navigate(`/consultant/${encodeURIComponent(data.personnel)}`);
    } else if (type === "project" && data?.project_id) {
      fetchProjectAnalysis(data.project_id);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  const nonFeePieData = nonFeeAnalysis?.overall?.categories 
    ? Object.entries(nonFeeAnalysis.overall.categories)
        .filter(([_, v]) => v.hours > 0)
        .map(([key, value]) => ({
          name: key.charAt(0).toUpperCase() + key.slice(1).replace("_", " "),
          value: value.hours,
          percentage: value.percentage
        }))
    : [];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="admin-dashboard">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">Admin</Badge>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Admin Dashboard</h1>
        <p className="text-muted-foreground mt-1">Manager assignments, non-fee analysis, AI insights, and user management</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex flex-wrap gap-2 w-full justify-start">
          <TabsTrigger value="assignments" className="gap-2"><UserPlus className="w-4 h-4" />Assign Project Manager</TabsTrigger>
          <TabsTrigger value="team-leaders" className="gap-2"><Users2 className="w-4 h-4" />Assign Team Leaders</TabsTrigger>
          <TabsTrigger value="non-fee" className="gap-2"><PieChart className="w-4 h-4" />Non-Fee Analysis</TabsTrigger>
          <TabsTrigger value="system" className="gap-2"><Shield className="w-4 h-4" />System</TabsTrigger>
          <TabsTrigger value="security" className="gap-2"><Lock className="w-4 h-4" />Security</TabsTrigger>
          <TabsTrigger value="sessions" className="gap-2"><Monitor className="w-4 h-4" />Sessions</TabsTrigger>
          <TabsTrigger value="audit-trail" className="gap-2"><BarChart3 className="w-4 h-4" />Audit Trail</TabsTrigger>
          <TabsTrigger value="ai" className="gap-2"><BotMessageSquare className="w-4 h-4" />AI Insights</TabsTrigger>
          <TabsTrigger value="users" className="gap-2"><Users className="w-4 h-4" />Users</TabsTrigger>
          <TabsTrigger value="visibility" className="gap-2"><EyeOff className="w-4 h-4" />Visibility Defaults</TabsTrigger>
        </TabsList>

        {/* Date range filter */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">From</Label>
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
                  className="h-9 w-44"
                  data-testid="admin-date-start"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">To</Label>
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
                  className="h-9 w-44"
                  data-testid="admin-date-end"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-9"
                onClick={() => setDateRange({ start: "", end: "" })}
                disabled={!dateRange.start && !dateRange.end}
                data-testid="admin-date-clear"
              >
                Clear
              </Button>
              <p className="text-xs text-muted-foreground">
                Date range filters time entries (cost/leakage/hours). Revenue comes from master project values.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Profitability Tab */}
        <TabsContent value="profitability" className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="metric-card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => {}}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Total Revenue</p>
                    <p className="text-3xl font-bold text-foreground tabular-nums">£{profitability?.summary?.project_revenue_total?.toLocaleString() || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-emerald-500/10"><DollarSign className="w-5 h-5 text-emerald-500" /></div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">From Project Master (project value) / budgets</p>
              </CardContent>
            </Card>

            <Card className="metric-card">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Billable Hours</p>
                    <p className="text-3xl font-bold text-foreground tabular-nums">{profitability?.summary?.total_billable_hours || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-blue-500/10"><Clock className="w-5 h-5 text-blue-500" /></div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">of {profitability?.summary?.total_hours || 0} total</p>
              </CardContent>
            </Card>

            <Card className="metric-card">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1 inline-flex items-center gap-1">
                      Avg Hourly Rate
                      <MetricInfo text="consultant_revenue_total / total_billable_hours (from timesheets)." />
                    </p>
                    <p className="text-3xl font-bold text-foreground tabular-nums">£{profitability?.summary?.average_hourly_rate || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-purple-500/10"><TrendingUp className="w-5 h-5 text-purple-500" /></div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Across billable hours</p>
              </CardContent>
            </Card>

            <Card className="metric-card">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1 inline-flex items-center gap-1">
                      Utilization
                      <MetricInfo text="(total_billable_hours / total_hours) × 100." />
                    </p>
                    <p className="text-3xl font-bold text-foreground tabular-nums">{profitability?.summary?.utilization_rate || 0}%</p>
                  </div>
                  <div className="p-3 rounded-lg bg-cyan-500/10"><TrendingUp className="w-5 h-5 text-cyan-500" /></div>
                </div>
                <Progress value={profitability?.summary?.utilization_rate || 0} className="mt-3 h-2" />
              </CardContent>
            </Card>

          </div>


          {/* Portfolio totals (profit, margin, leakage) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="metric-card">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1 inline-flex items-center gap-1">
                      Total Profit
                      <MetricInfo text="Sum across projects: revenue (master project value) − actual_cost (all hours × hourly cost)." />
                    </p>
                    <p className="text-2xl font-bold text-foreground tabular-nums">£{profitability?.projects_overall?.total_profit?.toLocaleString() || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-emerald-500/10"><TrendingUp className="w-5 h-5 text-emerald-500" /></div>
                </div>
              </CardContent>
            </Card>

            <Card className="metric-card">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1 inline-flex items-center gap-1">
                      Profit Margin
                      <MetricInfo text="(Total Profit / Total Revenue) × 100. Total Revenue comes from master project values." />
                    </p>
                    <p className="text-2xl font-bold text-foreground tabular-nums">{profitability?.projects_overall?.profit_margin || 0}%</p>
                  </div>
                  <div className="p-3 rounded-lg bg-cyan-500/10"><PieChart className="w-5 h-5 text-cyan-500" /></div>
                </div>
              </CardContent>
            </Card>

            <Card className="metric-card">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1 inline-flex items-center gap-1">
                      Cost Leakage
                      <MetricInfo text="Non-chargeable cost: Σ(non-chargeable hours × hourly cost). Non-chargeable hours = fee_earning=false." />
                    </p>
                    <p className="text-2xl font-bold text-foreground tabular-nums">£{profitability?.projects_overall?.total_leakage?.toLocaleString() || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-500/10"><DollarSign className="w-5 h-5 text-amber-500" /></div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Non-chargeable hours × hourly cost</p>
              </CardContent>
            </Card>
          </div>

          {/* Status summary */}
          {Array.isArray(profitability?.by_status) && profitability.by_status.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-semibold">Project Status Summary</CardTitle>
                <CardDescription>
                  Counts and totals grouped by master project status (Completed/Ongoing/Pipeline/etc.)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-muted-foreground">
                        <th className="text-left py-2 pr-3 font-medium">Status</th>
                        <th className="text-right py-2 px-3 font-medium">Projects</th>
                        <th className="text-right py-2 px-3 font-medium">Revenue</th>
                        <th className="text-right py-2 px-3 font-medium">Cost</th>
                        <th className="text-right py-2 px-3 font-medium">Profit</th>
                        <th className="text-right py-2 px-3 font-medium">Margin</th>
                        <th className="text-right py-2 px-3 font-medium">Leakage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profitability.by_status.map((row) => (
                        <tr key={row.status} className="border-b border-border/50">
                          <td className="py-2 pr-3 font-medium">{row.status}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{row.projects}</td>
                          <td className="py-2 px-3 text-right tabular-nums">£{(row.revenue || 0).toLocaleString()}</td>
                          <td className="py-2 px-3 text-right tabular-nums">£{(row.cost || 0).toLocaleString()}</td>
                          <td className="py-2 px-3 text-right tabular-nums">£{(row.profit || 0).toLocaleString()}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{row.profit_margin || 0}%</td>
                          <td className="py-2 px-3 text-right tabular-nums">£{(row.leakage || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}


          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="metric-card">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Consultant Revenue</p>
                    <p className="text-2xl font-bold text-foreground tabular-nums">£{profitability?.summary?.consultant_revenue_total?.toLocaleString() || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-emerald-500/10"><DollarSign className="w-5 h-5 text-emerald-500" /></div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Billable hours × consultant hourly rates</p>
              </CardContent>
            </Card>

            <Card className="metric-card">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Project Revenue</p>
                    <p className="text-2xl font-bold text-foreground tabular-nums">£{profitability?.summary?.project_revenue_total?.toLocaleString() || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-cyan-500/10"><Briefcase className="w-5 h-5 text-cyan-500" /></div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">From Project Master (project value)</p>
              </CardContent>
            </Card>
          </div>


          {/* Revenue by Consultant - Clickable */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">Revenue by Consultant</CardTitle>

      {/* Session-only filters (exclude admin/leave projects, etc.) */}
      <div className="flex flex-wrap items-center gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <Users className="w-4 h-4" />
              Consultants
              <Badge variant="secondary">{selectedConsultants.length}/{filterOptions.consultants?.length || 0}</Badge>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 max-h-96 overflow-auto" align="start">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Consultants</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const all = filterOptions.consultants || [];
                    if (selectedConsultants.length === all.length) setSelectedConsultants([]);
                    else setSelectedConsultants(all);
                  }}
                >
                  {selectedConsultants.length === (filterOptions.consultants?.length || 0) ? "Clear" : "Select all"}
                </Button>
              </div>
              <div className="space-y-2">
                {(filterOptions.consultants || []).map((c) => (
                  <label key={c} className="flex items-center justify-between text-sm">
                    <span className="truncate pr-2">{c}</span>
                    <input
                      type="checkbox"
                      checked={selectedConsultants.includes(c)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedConsultants((prev) => {
                          const s = new Set(prev);
                          if (checked) s.add(c);
                          else s.delete(c);
                          return Array.from(s);
                        });
                      }}
                    />
                  </label>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <Briefcase className="w-4 h-4" />
              Projects
              <Badge variant="secondary">{selectedProjects.length}/{filterOptions.projects?.length || 0}</Badge>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-96 max-h-96 overflow-auto" align="start">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Projects</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const all = filterOptions.projects || [];
                    if (selectedProjects.length === all.length) setSelectedProjects([]);
                    else setSelectedProjects(all);
                  }}
                >
                  {selectedProjects.length === (filterOptions.projects?.length || 0) ? "Clear" : "Select all"}
                </Button>
              </div>
              <div className="space-y-2">
                {(filterOptions.projects || []).map((p) => (
                  <label key={p} className="flex items-center justify-between text-sm">
                    <span className="truncate pr-2">{p}</span>
                    <input
                      type="checkbox"
                      checked={selectedProjects.includes(p)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedProjects((prev) => {
                          const s = new Set(prev);
                          if (checked) s.add(p);
                          else s.delete(p);
                          return Array.from(s);
                        });
                      }}
                    />
                  </label>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

              <CardDescription>Click on a consultant to view their details</CardDescription>
            </CardHeader>
            <CardContent>
              {profitability?.by_consultant?.length > 0 ? (
                <div className="space-y-4">
                  {profitability.by_consultant.map((consultant) => (
                    <div 
                      key={consultant.personnel} 
                      className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/consultant/${encodeURIComponent(consultant.personnel)}`)}
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <span className="text-sm font-semibold text-primary">
                          {consultant.personnel.split(" ").map(n => n[0]).join("")}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium">{consultant.personnel}</span>
                          <span className="text-sm font-semibold">£{consultant.revenue?.toLocaleString() || 0}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Progress value={(consultant.billable_hours / consultant.total_hours * 100) || 0} className="h-2 flex-1" />
                          <span className="text-xs text-muted-foreground w-24">{consultant.billable_hours}h / {consultant.total_hours}h</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Rate: £{consultant.hourly_rate}/hr</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">Set consultant rates in &quot;Rates &amp; Budgets&quot; tab to see revenue</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Projects Tab */}
        <TabsContent value="projects" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Project List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-semibold">Projects Profitability</CardTitle>
                <CardDescription>Select projects to see detailed profit and cost breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Project search and overall multi-project summary */}
                <div className="flex items-center justify-between gap-3 mb-3">
                  <Input
                    placeholder="Search projects by number, name, client, or status"
                    className="max-w-xs"
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                  />
                </div>

                {/* Overall multi-project summary */}
                <div className="mb-4 grid grid-cols-2 gap-3 text-sm">
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">Total Revenue</p>
                    <p className="text-lg font-semibold">£{profitability?.projects_overall?.total_revenue ?? 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">Total Cost</p>
                    <p className="text-lg font-semibold">£{profitability?.projects_overall?.total_cost ?? 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-emerald-500/10">
                    <p className="text-xs text-muted-foreground">Total Profit</p>
                    <p className="text-lg font-semibold text-emerald-600">£{profitability?.projects_overall?.total_profit ?? 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-indigo-500/10">
                    <p className="text-xs text-muted-foreground">Profit Margin</p>
                    <p className="text-lg font-semibold text-indigo-600">{profitability?.projects_overall?.profit_margin ?? 0}%</p>
                  </div>
                </div>

                <DataTable
                  data={profitability?.by_project || []}
                  searchFields={["project_id", "project_name", "client_name", "company", "status", "business_area"]}
                  columns={[
                    { key: "project_id", label: "Project", render: (p) => {
                      const lbl = p.project_id && p.company ? `${p.project_id} – ${p.company}` : p.project_id || p.company || "Unnamed";
                      return <span className="font-medium truncate block max-w-[200px]" title={lbl}>{lbl}</span>;
                    }},
                    { key: "status", label: "Status", cellClassName: "text-muted-foreground" },
                    { key: "business_area", label: "Business Area", cellClassName: "text-muted-foreground" },
                    { key: "client_name", label: "Client", cellClassName: "text-muted-foreground", render: (p) => p.client_name || p.company },
                    { key: "revenue", label: "Revenue", cellClassName: "text-right tabular-nums", render: (p) => `£${(p.revenue ?? p.budget_amount ?? 0).toLocaleString()}` },
                    { key: "actual_cost", label: "Cost", cellClassName: "text-right tabular-nums", render: (p) => `£${(p.actual_cost ?? 0).toLocaleString()}` },
                    { key: "profit", label: "Profit", render: (p) => <span className={`tabular-nums ${(p.profit || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>£{(p.profit ?? 0).toLocaleString()}</span> },
                    { key: "profit_margin", label: "Margin", cellClassName: "text-right tabular-nums", render: (p) => `${(p.profit_margin ?? 0).toFixed(1)}%` },
                    { key: "chargeable_hours", label: "Chargeable h", cellClassName: "text-right tabular-nums text-emerald-600", render: (p) => `${p.chargeable_hours ?? p.fee_earning_hours ?? 0}h` },
                    { key: "non_chargeable_hours", label: "Non-chargeable h", cellClassName: "text-right tabular-nums text-amber-600", render: (p) => `${p.non_chargeable_hours ?? p.non_fee_hours ?? 0}h` },
                  ]}
                  defaultSort="revenue"
                  onRowClick={(p) => fetchProjectAnalysis(p.project_id)}
                  rowClassName={(p) => selectedProject === p.project_id ? "bg-primary/5" : ""}
                  searchPlaceholder="Search projects..."
                  emptyMessage="No project data"
                />
              </CardContent>
            </Card>

            {/* Project Analysis */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-semibold">Project Analysis</CardTitle>
                <CardDescription>{selectedProject || "Select a project"}</CardDescription>
              </CardHeader>
              <CardContent>
                {projectAnalysis ? (
                  <div className="space-y-6">
                    {/* Summary */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 rounded-lg bg-muted/50">
                        <p className="text-xs text-muted-foreground">Total Hours</p>
                        <p className="text-xl font-bold">{projectAnalysis.summary?.total_hours}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/50">
                        <p className="text-xs text-muted-foreground">Total Cost</p>
                        <p className="text-xl font-bold">£{projectAnalysis.summary?.total_cost}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-emerald-500/10">
                        <p className="text-xs text-muted-foreground">Chargeable Time</p>
                        <p className="text-xl font-bold text-emerald-600">{projectAnalysis.summary?.fee_earning_hours}h</p>
                      </div>
                      <div className="p-3 rounded-lg bg-amber-500/10">
                        <p className="text-xs text-muted-foreground">Non-Chargeable Time</p>
                        <p className="text-xl font-bold text-amber-600">{projectAnalysis.summary?.non_fee_hours}h</p>
                      </div>
                    </div>

                    {/* Budget vs Actual */}
                    {projectAnalysis.budget && (
                      <div className="p-4 rounded-lg border border-border">
                        <h4 className="font-medium mb-3">Budget vs Actual</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Budget: {projectAnalysis.summary?.budget_hours}h</span>
                            <span>Actual: {projectAnalysis.summary?.total_hours}h</span>
                          </div>
                          <Progress value={projectAnalysis.summary?.utilization_pct || 0} className="h-2" />
                          <p className={`text-sm ${projectAnalysis.summary?.hours_variance < 0 ? "text-red-600" : "text-emerald-600"}`}>
                            Variance: {projectAnalysis.summary?.hours_variance}h
                          </p>
                        </div>
                      </div>
                    )}



                    {/* Quoted vs Actual (Prep/Delivery) */}
                    {(projectAnalysis.summary?.prep_hours_quoted > 0 || projectAnalysis.summary?.delivery_hours_quoted > 0) && (
                      <div className="p-4 rounded-lg border border-border">
                        <h4 className="font-medium mb-3">Quoted vs Actual (Prep / Delivery)</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                          <div className="p-3 rounded-lg bg-muted/40">
                            <p className="text-xs text-muted-foreground">Prep Hours</p>
                            <p className="font-medium">Quoted: {projectAnalysis.summary?.prep_hours_quoted}h</p>
                            <p>Actual: {projectAnalysis.summary?.prep_hours}h</p>
                            <p className={`text-xs ${projectAnalysis.summary?.prep_hours_variance < 0 ? "text-red-600" : "text-emerald-600"}`}>
                              Variance: {projectAnalysis.summary?.prep_hours_variance}h
                            </p>
                          </div>
                          <div className="p-3 rounded-lg bg-muted/40">
                            <p className="text-xs text-muted-foreground">Delivery Hours</p>
                            <p className="font-medium">Quoted: {projectAnalysis.summary?.delivery_hours_quoted}h</p>
                            <p>Actual: {projectAnalysis.summary?.delivery_hours}h</p>
                            <p className={`text-xs ${projectAnalysis.summary?.delivery_hours_variance < 0 ? "text-red-600" : "text-emerald-600"}`}>
                              Variance: {projectAnalysis.summary?.delivery_hours_variance}h
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Personnel Breakdown */}
                    <div>
                      <h4 className="font-medium mb-3">Team Members</h4>
                      <div className="space-y-2">
                        {projectAnalysis.by_personnel?.map((person) => (
                          <div 
                            key={person.personnel} 
                            className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 cursor-pointer"
                            onClick={() => navigate(`/consultant/${encodeURIComponent(person.personnel)}`)}
                          >
                            <div>
                              <p className="font-medium">{person.personnel}</p>
                              <p className="text-xs text-muted-foreground">£{person.hourly_rate}/hr</p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium">{person.total_hours}h</p>
                              <p className="text-xs text-muted-foreground">£{person.cost}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64 text-muted-foreground">
                    <p>Select a project to view analysis</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Non-Fee Analysis Tab */}
        <TabsContent value="non-fee" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-semibold">Non-Fee Time Distribution</CardTitle>
                <CardDescription>Total: {nonFeeAnalysis?.overall?.total_non_fee_hours || 0} hours</CardDescription>
              </CardHeader>
              <CardContent>
                {nonFeePieData.length > 0 ? (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPie>
                        <Pie data={nonFeePieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value" label={({ percentage }) => `${percentage}%`} labelLine={false}>
                          {nonFeePieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CATEGORY_COLORS[entry.name.toLowerCase().replace(" ", "_")] || "#94A3B8"} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </RechartsPie>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-16">No non-fee hours recorded</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base font-semibold">Activity Categories</CardTitle>
                <CardDescription>Auto-categorized from descriptions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {nonFeeAnalysis?.overall?.categories && 
                    Object.entries(nonFeeAnalysis.overall.categories)
                      .filter(([_, v]) => v.hours > 0)
                      .sort((a, b) => b[1].hours - a[1].hours)
                      .map(([category, data]) => {
                        const Icon = CATEGORY_ICONS[category] || MoreHorizontal;
                        return (
                          <div key={category} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                            <div className="p-2 rounded-lg" style={{ backgroundColor: `${CATEGORY_COLORS[category]}20` }}>
                              <Icon className="w-4 h-4" style={{ color: CATEGORY_COLORS[category] }} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className="font-medium capitalize">{category.replace("_", " ")}</span>
                                <span className="text-sm font-semibold">{data.hours}h</span>
                              </div>
                              <div className="flex items-center justify-between mt-1">
                                <span className="text-xs text-muted-foreground">{data.count} entries</span>
                                <span className="text-xs text-muted-foreground">{data.percentage}%</span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                  }
                  {(!nonFeeAnalysis?.overall?.categories || Object.values(nonFeeAnalysis.overall.categories).every(v => v.hours === 0)) && (
                    <p className="text-center text-muted-foreground py-8">No non-fee hours to analyze</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* By Consultant Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">Non-Fee Time by Consultant</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th>Consultant</th>
                      <th>Total Non-Fee</th>
                      <th>Admin</th>
                      <th>Meetings</th>
                      <th>Training</th>
                      <th>Leave</th>
                      <th>Other</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nonFeeAnalysis?.by_consultant && Object.entries(nonFeeAnalysis.by_consultant).map(([name, data]) => (
                      <tr key={name} className="cursor-pointer hover:bg-muted/30" onClick={() => navigate(`/consultant/${encodeURIComponent(name)}`)}>
                        <td className="font-medium">{name}</td>
                        <td className="tabular-nums">{data.total_non_fee_hours}h</td>
                        <td className="tabular-nums">{data.categories?.admin?.hours || 0}h</td>
                        <td className="tabular-nums">{data.categories?.meetings?.hours || 0}h</td>
                        <td className="tabular-nums">{data.categories?.training?.hours || 0}h</td>
                        <td className="tabular-nums">{data.categories?.leave?.hours || 0}h</td>
                        <td className="tabular-nums">{data.categories?.other?.hours || 0}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rates & Budgets Tab */}
        <TabsContent value="rates" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">Project Budgets</CardTitle>
              <CardDescription>Set budget hours and amounts for project tracking. Consultant pay rates are managed by directors in Company P&L &gt; Employees &amp; Pay.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Select value={newBudget.project_id} onValueChange={(v) => setNewBudget({ ...newBudget, project_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>
                        {p.project_id} ({p.company})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Input type="number" step="0.01" min="0" placeholder="Budget hours" value={newBudget.budget_hours} onChange={(e) => setNewBudget({ ...newBudget, budget_hours: e.target.value })} />
                  <Input type="number" step="0.01" min="0" placeholder="Budget £" value={newBudget.budget_amount} onChange={(e) => setNewBudget({ ...newBudget, budget_amount: e.target.value })} />
                </div>
                <Button onClick={handleSaveBudget} className="w-full gap-2">
                  <Plus className="w-4 h-4" /> Add Budget
                </Button>
              </div>
              <div className="space-y-2">
                {projectBudgets.map((budget) => (
                  <div key={budget.project_id} className="p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center justify-between">
                      <span className="font-medium truncate">{budget.project_id}</span>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteBudget(budget.project_id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <div className="flex gap-4 mt-1 text-sm text-muted-foreground">
                      <span>{budget.budget_hours}h budget</span>
                      {budget.budget_amount > 0 && <span>£{budget.budget_amount}</span>}
                    </div>
                  </div>
                ))}
                {projectBudgets.length === 0 && (
                  <p className="text-center text-muted-foreground py-4">No budgets set yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Duplicate AI Insights Tab removed - actual implementation below */}

        {/* System Health & Security Tab */}
        <TabsContent value="system" className="space-y-6">
          {systemHealth ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className={`rounded-xl border ${systemHealth.status === "healthy" ? "border-emerald-200 bg-emerald-50/50 dark:bg-emerald-900/20" : "border-red-200 bg-red-50/50"}`}>
                  <CardContent className="pt-6">
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">System Status</p>
                    <p className={`text-2xl font-bold ${systemHealth.status === "healthy" ? "text-emerald-600" : "text-red-600"}`}>{systemHealth.status === "healthy" ? "Healthy" : "Degraded"}</p>
                    <p className="text-xs text-muted-foreground mt-1">v{systemHealth.version}</p>
                  </CardContent>
                </Card>
                <Card className="rounded-xl"><CardContent className="pt-6">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Database</p>
                  <p className="text-2xl font-bold tabular-nums">{systemHealth.database?.size_mb || 0} MB</p>
                  <p className="text-xs text-muted-foreground mt-1">{systemHealth.database?.collections || 0} collections</p>
                </CardContent></Card>
                <Card className="rounded-xl"><CardContent className="pt-6">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Disk Usage</p>
                  <p className="text-2xl font-bold tabular-nums">{systemHealth.disk?.usage_pct || 0}%</p>
                  <Progress value={systemHealth.disk?.usage_pct || 0} className="mt-2 h-2" />
                  <p className="text-xs text-muted-foreground mt-1">{systemHealth.disk?.free_gb || 0} GB free</p>
                </CardContent></Card>
                <Card className="rounded-xl"><CardContent className="pt-6">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Platform</p>
                  <p className="text-sm font-medium">Python {systemHealth.python}</p>
                  <p className="text-xs text-muted-foreground mt-1 truncate">{systemHealth.platform}</p>
                </CardContent></Card>
              </div>

              {/* Security Overview */}
              {systemSecurity && (
                <Card>
                  <CardHeader><CardTitle className="text-base font-semibold">Security Overview</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                      <div className="p-3 rounded-lg bg-muted/50"><p className="text-xs text-muted-foreground">Total Users</p><p className="text-xl font-bold tabular-nums">{systemSecurity.total_users}</p></div>
                      <div className="p-3 rounded-lg bg-muted/50"><p className="text-xs text-muted-foreground">Active</p><p className="text-xl font-bold text-emerald-600 tabular-nums">{systemSecurity.active_users}</p></div>
                      <div className="p-3 rounded-lg bg-muted/50"><p className="text-xs text-muted-foreground">Inactive</p><p className="text-xl font-bold text-red-600 tabular-nums">{systemSecurity.inactive_users}</p></div>
                      <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20"><p className="text-xs text-amber-600">Must Change Password</p><p className="text-xl font-bold text-amber-600 tabular-nums">{systemSecurity.must_change_password}</p></div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(systemSecurity.roles || {}).map(([role, count]) => (
                        <Badge key={role} variant="secondary" className="text-xs">{role}: {count}</Badge>
                      ))}
                    </div>
                    <div className="mt-4 p-3 rounded-lg bg-muted/50">
                      <p className="text-xs font-medium mb-2">Password Policy</p>
                      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                        <span>Min 10 chars</span><span>•</span><span>Uppercase</span><span>•</span><span>Lowercase</span><span>•</span><span>Number</span><span>•</span><span>Special char</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Database Collections */}
              <Card>
                <CardHeader><CardTitle className="text-base font-semibold">Database Collections</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-border"><th className="text-left py-2 px-3 text-xs font-semibold uppercase text-muted-foreground">Collection</th><th className="text-right py-2 px-3 text-xs font-semibold uppercase text-muted-foreground">Documents</th></tr></thead>
                      <tbody>{(systemHealth.database?.details || []).map(c => (
                        <tr key={c.name} className="border-b border-border/50"><td className="py-2 px-3 font-medium font-mono text-xs">{c.name}</td><td className="py-2 px-3 text-right tabular-nums">{c.documents}</td></tr>
                      ))}</tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Backup & Maintenance */}
              <Card>
                <CardHeader><CardTitle className="text-base font-semibold">Backup & Maintenance</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-lg border border-border">
                    <div>
                      <p className="font-medium">Export Full Backup</p>
                      <p className="text-sm text-muted-foreground">Download all data as JSON (excludes passwords)</p>
                    </div>
                    <Button variant="outline" className="gap-2" onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/admin/system/backup`, { responseType: "blob" });
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const link = document.createElement("a");
                        link.href = url;
                        link.setAttribute("download", `backup_${new Date().toISOString().split("T")[0]}.json`);
                        document.body.appendChild(link); link.click(); link.remove();
                        toast.success("Backup downloaded");
                      } catch { toast.error("Backup failed"); }
                    }}>Download Backup</Button>
                  </div>

                  {/* Audit Log */}
                  {auditLog && (
                    <div>
                      <p className="text-sm font-medium mb-2">Recent Activity</p>
                      <div className="space-y-2">
                        {(auditLog.recent_users || []).slice(0, 5).map(u => (
                          <div key={u.id} className="flex items-center justify-between p-2 rounded bg-muted/30 text-sm">
                            <div><span className="font-medium">{u.name}</span> <span className="text-muted-foreground">({u.role})</span></div>
                            <span className="text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="border-dashed"><CardContent className="flex flex-col items-center justify-center py-16">
              <Shield className="w-12 h-12 text-muted-foreground mb-4" /><h3 className="text-lg font-semibold mb-2">Loading system health...</h3>
            </CardContent></Card>
          )}
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-6">
          {/* Risk Assessment */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-semibold">User Risk Assessment</CardTitle>
                  <CardDescription>Users with security risks: no 2FA, failed logins, unfamiliar IPs, multiple countries</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={async () => {
                    try { await axios.post(`${API}/admin/security/test-email`); toast.success("Test email sent"); } catch { toast.error("Failed"); }
                  }}>Test Email Alert</Button>
                  <Button variant="outline" size="sm" onClick={async () => {
                    try {
                      const { downloadFile: dl } = await import("../lib/download");
                      await dl(`${API}/export/security-report`);
                      toast.success("Security report downloaded");
                    } catch { toast.error("Download failed"); }
                  }}>Security Report PDF</Button>
                  <Button variant="outline" size="sm" id="load-risk-btn" onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/admin/security/risk-assessment`);
                      const container = document.getElementById("risk-container");
                      if (!container) return;
                      const users = res.data.users || [];
                      container.innerHTML = users.map(u => {
                        const riskColor = u.risk_level === "critical" ? "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800" : u.risk_level === "high" ? "bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800" : u.risk_level === "medium" ? "bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20" : "bg-emerald-50 border-emerald-200 dark:bg-emerald-900/10";
                        const scoreColor = u.risk_score >= 50 ? "bg-red-500" : u.risk_score >= 30 ? "bg-amber-500" : u.risk_score >= 15 ? "bg-yellow-500" : "bg-emerald-500";
                        return `<div class="p-3 rounded-lg border ${riskColor} space-y-2">
                          <div class="flex items-center justify-between">
                            <div class="flex items-center gap-3">
                              <div class="w-8 h-8 rounded-full ${scoreColor} text-white flex items-center justify-center text-xs font-bold">${u.risk_score}</div>
                              <div>
                                <p class="font-medium text-sm">${u.name} <span class="text-xs text-muted-foreground">(${u.role})</span></p>
                                <p class="text-xs text-muted-foreground">${u.email}</p>
                              </div>
                            </div>
                            <span class="text-xs font-medium uppercase ${u.risk_level === 'critical' ? 'text-red-600' : u.risk_level === 'high' ? 'text-amber-600' : 'text-emerald-600'}">${u.risk_level}</span>
                          </div>
                          ${u.risks.length > 0 ? `<div class="flex flex-wrap gap-1">${u.risks.map(r => `<span class="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">${r}</span>`).join('')}</div>` : ''}
                          ${u.last_login_time ? `<div class="text-[10px] text-muted-foreground">Last: ${new Date(u.last_login_time).toLocaleString()} | ${u.last_login_city || 'Unknown'}, ${u.last_login_country || 'Unknown'} | ${u.last_login_device || ''} ${u.last_login_browser || ''} | IP: ${u.last_login_ip || ''}</div>` : '<div class="text-[10px] text-muted-foreground">Never logged in</div>'}
                        </div>`;
                      }).join('') || '<p class="text-center text-muted-foreground py-4">No users</p>';
                      toast.success(`${users.length} users assessed - ${res.data.critical_count} critical, ${res.data.high_count} high risk`);
                    } catch { toast.error("Failed to load"); }
                  }}>Load Risk Assessment</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[400px] overflow-y-auto" id="risk-container">
                <p className="text-sm text-muted-foreground text-center py-4">Click "Load Risk Assessment" to analyze users</p>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Audit Log with Filters */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle className="text-base font-semibold">Security Audit Log</CardTitle>
                    <CardDescription>Login attempts with IP, location, device, browser - last 7 days</CardDescription>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <select id="log-days" className="h-8 rounded border border-border bg-background px-2 text-xs" defaultValue="7">
                      <option value="1">Last 24h</option>
                      <option value="3">Last 3 days</option>
                      <option value="7">Last 7 days</option>
                      <option value="14">Last 14 days</option>
                      <option value="30">Last 30 days</option>
                    </select>
                    <select id="log-action" className="h-8 rounded border border-border bg-background px-2 text-xs">
                      <option value="">All actions</option>
                      <option value="login_success">Login success</option>
                      <option value="login_failed">Login failed</option>
                      <option value="2fa_failed">2FA failed</option>
                      <option value="2fa_locked">2FA locked</option>
                      <option value="sessions_revoked">Sessions revoked</option>
                      <option value="account_unlocked">Account unlocked</option>
                    </select>
                    <Input id="log-email" placeholder="Filter by email" className="h-8 w-40 text-xs" />
                    <Button variant="default" size="sm" onClick={async () => {
                      const days = document.getElementById("log-days")?.value || "7";
                      const action = document.getElementById("log-action")?.value || "";
                      const emailFilter = document.getElementById("log-email")?.value || "";
                      try {
                        const params = new URLSearchParams({ days, limit: "200" });
                        if (action) params.set("action", action);
                        if (emailFilter) params.set("email", emailFilter);
                        const res = await axios.get(`${API}/admin/security/audit-log?${params.toString()}`);
                        const container = document.getElementById("audit-log-container");
                        const statsContainer = document.getElementById("audit-stats");
                        
                        // Stats
                        if (statsContainer && res.data.stats) {
                          const s = res.data.stats;
                          statsContainer.innerHTML = Object.entries(s).map(([k,v]) => 
                            `<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${k.includes('failed') || k.includes('locked') ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : k.includes('success') ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-muted text-foreground'}">${k}: ${v}</span>`
                          ).join(' ');
                        }
                        
                        // Logs
                        if (container) {
                          container.innerHTML = `<p class="text-xs text-muted-foreground mb-2">Showing ${res.data.showing} of ${res.data.total} entries (${days} days)</p>` +
                          (res.data.logs || []).map(l => {
                            const isFailure = l.action.includes('failed') || l.action.includes('locked');
                            const isSuccess = l.action.includes('success');
                            const bgClass = isFailure ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800' : isSuccess ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800' : 'bg-muted/30 border-border';
                            return `<div class="p-2.5 rounded-lg text-xs border ${bgClass} space-y-1">
                              <div class="flex items-center justify-between gap-2">
                                <span class="font-semibold ${isFailure ? 'text-red-700 dark:text-red-400' : isSuccess ? 'text-emerald-700 dark:text-emerald-400' : ''}">${l.action}</span>
                                <span class="text-muted-foreground whitespace-nowrap">${l.timestamp ? new Date(l.timestamp).toLocaleString() : ''}</span>
                              </div>
                              <div class="flex flex-wrap gap-x-3 gap-y-0.5 text-muted-foreground">
                                ${l.email ? `<span><b>${l.email}</b></span>` : ''}
                                ${l.ip ? `<span>IP: <code class="bg-muted px-1 rounded">${l.ip}</code></span>` : ''}
                                ${l.city && l.city !== 'Unknown' ? `<span>${l.city}, ${l.country || ''}</span>` : l.country ? `<span>${l.country}</span>` : ''}
                                ${l.device && l.device !== 'Unknown' ? `<span>${l.device}</span>` : ''}
                                ${l.browser && l.browser !== 'Unknown' ? `<span>${l.browser}</span>` : ''}
                                ${l.os && l.os !== 'Unknown' ? `<span>${l.os}</span>` : ''}
                                ${l.isp ? `<span class="text-[10px]">ISP: ${l.isp}</span>` : ''}
                                ${l.details ? `<span class="italic">${l.details}</span>` : ''}
                              </div>
                            </div>`;
                          }).join('') || '<p class="text-center text-muted-foreground py-4">No logs found</p>';
                        }
                        toast.success(`Loaded ${res.data.showing} entries`);
                      } catch { toast.error("Failed to load"); }
                    }}>Load Logs</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1 mb-3" id="audit-stats"></div>
                <div className="space-y-2 max-h-[500px] overflow-y-auto" id="audit-log-container">
                  <p className="text-sm text-muted-foreground text-center py-4">Select filters and click "Load Logs"</p>
                </div>
              </CardContent>
            </Card>

            {/* IP Management + Notification Emails */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-semibold">IP & Notification Management</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium mb-2">Blocked IPs</h4>
                  <div className="space-y-1" id="ip-blacklist-container"><p className="text-sm text-muted-foreground">Click load</p></div>
                  <Button variant="outline" size="sm" className="mt-2" onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/admin/security/ip-blacklist`);
                      const ips = res.data.blocked_ips || [];
                      const c = document.getElementById("ip-blacklist-container");
                      if (c) c.innerHTML = ips.length === 0 ? '<p class="text-sm text-emerald-600">No blocked IPs</p>' : ips.map(ip => `<div class="flex items-center justify-between p-2 rounded bg-red-50 dark:bg-red-900/10 border border-red-200 text-xs"><span class="font-mono">${ip.ip}</span><span>${ip.reason || ''} · ${ip.blocked_at ? new Date(ip.blocked_at).toLocaleDateString() : ''}</span></div>`).join('');
                    } catch { toast.error("Failed"); }
                  }}>Load</Button>
                </div>
                <div className="border-t border-border pt-3">
                  <h4 className="text-sm font-medium mb-2">Locked Accounts</h4>
                  <div className="space-y-1 mb-2" id="locked-accounts-container"><p className="text-xs text-muted-foreground">Click load</p></div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/admin/security/risk-assessment`);
                        const locked = (res.data.users || []).filter(u => u.risks.some(r => r.includes("locked")));
                        const c = document.getElementById("locked-accounts-container");
                        if (c) {
                          c.innerHTML = locked.length === 0 ? '<p class="text-xs text-emerald-600">No locked accounts</p>' : locked.map(u =>
                            `<div class="flex items-center justify-between p-2 rounded bg-red-50 dark:bg-red-900/10 border border-red-200 text-xs">
                              <div><b>${u.name}</b> (${u.email})</div>
                              <button onclick="(async()=>{try{await fetch('${API}/admin/security/unlock-account',{method:'POST',headers:{'Content-Type':'application/json','Authorization':'Bearer '+localStorage.getItem('auth_token')},body:JSON.stringify({email:'${u.email}'})});this.parentElement.remove();alert('Unlocked: ${u.email}');}catch{alert('Failed')}})()" class="px-2 py-1 bg-emerald-500 text-white rounded hover:bg-emerald-600 text-xs">Unlock</button>
                            </div>`
                          ).join('');
                        }
                      } catch { toast.error("Failed"); }
                    }}>Load Locked Accounts</Button>
                  </div>
                </div>
                <div className="border-t border-border pt-3">
                  <h4 className="text-sm font-medium mb-2">Manual Unlock</h4>
                  <div className="flex gap-2">
                    <Input id="unlock-email" placeholder="email@company.com" className="flex-1 h-8 text-xs" />
                    <Button variant="default" size="sm" onClick={async () => {
                      const v = document.getElementById("unlock-email")?.value;
                      if (!v) { toast.error("Enter email"); return; }
                      try {
                        const res = await axios.post(`${API}/admin/security/unlock-account`, { email: v });
                        toast.success(res.data.message);
                        document.getElementById("unlock-email").value = "";
                      } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                    }}>Unlock Account</Button>
                  </div>
                </div>
                <div className="border-t border-border pt-3">
                  <h4 className="text-sm font-medium mb-2">Unblock IP</h4>
                  <div className="flex gap-2">
                    <Input id="unblock-ip" placeholder="IP address" className="flex-1 h-8 text-xs" />
                    <Button variant="outline" size="sm" onClick={async () => { const v = document.getElementById("unblock-ip")?.value; if (!v) return; try { await axios.post(`${API}/admin/security/ip-unblock`, { ip: v }); toast.success("Unblocked"); } catch { toast.error("Failed"); } }}>Unblock</Button>
                  </div>
                </div>
                <div className="border-t border-border pt-3">
                  <h4 className="text-sm font-medium mb-2">Alert Notification Emails</h4>
                  <p className="text-xs text-muted-foreground mb-2">Default: accounts@spillconsult.com, info@spillconsult.com</p>
                  <div className="flex gap-2">
                    <Input id="extra-emails" placeholder="extra@company.com, another@company.com" className="flex-1 h-8 text-xs" />
                    <Button variant="outline" size="sm" onClick={async () => {
                      const v = document.getElementById("extra-emails")?.value || "";
                      const emails = v.split(",").map(e => e.trim()).filter(Boolean);
                      try { await axios.post(`${API}/admin/security/notification-emails`, { emails }); toast.success("Emails updated"); } catch { toast.error("Failed"); }
                    }}>Save</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Active Security Features - Real-time */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold">Security Features — Live Status</CardTitle>
                <Button variant="ghost" size="sm" onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/admin/system/security-features`);
                    setSecurityFeatures(res.data?.features || []);
                  } catch {}
                }} data-testid="refresh-security-btn">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {(securityFeatures.length > 0 ? securityFeatures : [
                  { name: "Brute Force Protection", desc: "5 attempts / 15 min", status: "active", detail: "" },
                  { name: "Account Lockout", desc: "30 min after 5 fails", status: "active", detail: "" },
                  { name: "Progressive Delay", desc: "1s → 8s backoff", status: "active", detail: "" },
                  { name: "2FA (TOTP)", desc: "Mandatory all users", status: "active", detail: "" },
                  { name: "bcrypt Hashing", desc: "Salted passwords", status: "active", detail: "" },
                  { name: "JWT 8hr Expiry", desc: "Auto-expire sessions", status: "active", detail: "" },
                  { name: "IP Auto-Blacklist", desc: "After 10 fails", status: "active", detail: "" },
                  { name: "Bot Detection", desc: "Honeypot + UA check", status: "active", detail: "" },
                  { name: "Security Headers", desc: "HSTS, XFO, CSP", status: "active", detail: "" },
                  { name: "Full Audit Log", desc: "IP, location, device", status: "active", detail: "" },
                  { name: "Email Alerts", desc: "Lockout + new IP", status: "active", detail: "" },
                  { name: "Risk Scoring", desc: "Per-user assessment", status: "active", detail: "" },
                  { name: "Geo-IP Tracking", desc: "Location per login", status: "active", detail: "" },
                  { name: "Unfamiliar IP Alert", desc: "New device email", status: "active", detail: "" },
                ]).map((f, i) => {
                  const colors = {
                    active: "bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800",
                    warning: "bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800",
                    issue: "bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800",
                  };
                  const textColors = {
                    active: "text-emerald-800 dark:text-emerald-300",
                    warning: "text-amber-800 dark:text-amber-300",
                    issue: "text-red-800 dark:text-red-300",
                  };
                  const dotColors = {
                    active: "bg-emerald-500",
                    warning: "bg-amber-500",
                    issue: "bg-red-500",
                  };
                  const status = f.status || "active";
                  return (
                    <div key={i} className={`p-2.5 rounded-lg border ${colors[status]}`} title={f.detail || ""}>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <div className={`w-2 h-2 rounded-full ${dotColors[status]} ${status === "active" ? "" : "animate-pulse"}`} />
                        <p className={`text-xs font-semibold ${textColors[status]}`}>{f.name}</p>
                      </div>
                      <p className={`text-[10px] ${status === "active" ? "text-emerald-600 dark:text-emerald-400" : status === "warning" ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400"}`}>
                        {f.detail || f.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="ai" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">AI Insights</CardTitle>
              <CardDescription>
                Ask AI questions about project profit, margin and time usage based on your latest data.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="ai-enabled"
                    checked={aiSettings.ai_enabled}
                    onCheckedChange={async (checked) => {
                      try {
                        const payload = {
                          ai_enabled: checked,
                          default_provider: aiSettings.default_provider || "openai",
                        };
                        const res = await axios.put(`${API}/admin/settings/ai`, payload);
                        setAiSettings(res.data.settings);
                        toast.success(checked ? "AI insights enabled" : "AI insights disabled");
                      } catch (error) {
                        toast.error("Failed to update AI settings");
                      }
                    }}
                  />
                  <Label htmlFor="ai-enabled" className="text-sm">Enable AI Insights</Label>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Provider</span>
                  <Select value={aiProvider} onValueChange={setAiProvider}>
                    <SelectTrigger className="w-32 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openai">OpenAI</SelectItem>
                      <SelectItem value="claude">Claude</SelectItem>
                      <SelectItem value="gemini">Gemini</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ai-question">Question</Label>
                <textarea
                  id="ai-question"
                  className="w-full min-h-[80px] rounded-md border border-border bg-background px-3 py-2 text-sm"
                  placeholder="e.g. Which projects are most at risk due to high non-chargeable time?"
                  value={aiQuestion}
                  onChange={(e) => setAiQuestion(e.target.value)}
                />
              </div>

              <div className="flex justify-end">
                <Button
                  size="sm"
                  disabled={!aiSettings.ai_enabled || !aiQuestion || aiLoading}
                  onClick={async () => {
                    try {
                      setAiLoading(true);
                      const res = await axios.post(`${API}/admin/ai/insights`, {
                        provider: aiProvider || aiSettings.default_provider || "openai",
                        question: aiQuestion,
                        scope: selectedProject ? "project" : "portfolio",
                        project_id: selectedProject || undefined,
                      });
                      setAiAnswer(res.data.answer || "No answer returned");
                    } catch (error) {
                      toast.error(error.response?.data?.detail || "Failed to get AI insights");
                    } finally {
                      setAiLoading(false);
                    }
                  }}
                >
                  {aiLoading ? "Asking AI..." : "Ask AI"}
                </Button>
              </div>

              {aiAnswer && (
                <div className="mt-2 p-3 rounded-md border border-border bg-muted/50 text-sm whitespace-pre-wrap">
                  {aiAnswer}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        {/* Project Assignments Tab */}
        <TabsContent value="assignments" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-base font-semibold">Project Manager Assignments</CardTitle>
                  <CardDescription>Assign project managers to specific projects. Project managers will see all time records, personnel, and analysis for assigned projects.</CardDescription>
                </div>
                <Dialog open={assignFormOpen} onOpenChange={setAssignFormOpen}>
                  <DialogTrigger asChild>
                    <Button className="gap-2"><UserPlus className="w-4 h-4" /> New Assignment</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Assign Projects to User</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                      <div className="space-y-2">
                        <Label>Select User (any role)</Label>
                        <Select value={assignManagerId} onValueChange={setAssignManagerId}>
                          <SelectTrigger><SelectValue placeholder="Choose a user (manager/director/admin/employee)" /></SelectTrigger>
                          <SelectContent>
                            {users.filter(u => u.is_active !== false).map(u => (
                              <SelectItem key={u.id} value={u.id}>{u.name} ({u.role}) - {u.email}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Select Projects</Label>
                        <Input placeholder="Search projects..." value={assignSearch} onChange={(e) => setAssignSearch(e.target.value)} className="mb-2" />
                        <div className="border rounded-md border-border p-3 max-h-60 overflow-auto">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-muted-foreground">{assignProjectIds.length} selected</span>
                            <Button variant="ghost" size="sm" onClick={() => {
                              if (assignProjectIds.length > 0) { setAssignProjectIds([]); setMasterQuotedHours({}); }
                              else { const all = projects.map(p => p.project_id); setAssignProjectIds(all); fetchMasterHours(all); }
                            }}>{assignProjectIds.length > 0 ? "Clear" : "Select all"}</Button>
                          </div>
                          {projects.filter(p => !assignSearch.trim() || `${p.project_id} ${p.company}`.toLowerCase().includes(assignSearch.toLowerCase())).map(p => (
                            <label key={p.project_id} className="flex items-center gap-2 py-1.5 text-sm hover:bg-muted/30 px-2 rounded">
                              <input type="checkbox" checked={assignProjectIds.includes(p.project_id)} onChange={(e) => {
                                let newIds;
                                if (e.target.checked) { newIds = [...assignProjectIds, p.project_id]; }
                                else { newIds = assignProjectIds.filter(id => id !== p.project_id); }
                                setAssignProjectIds(newIds);
                                fetchMasterHours(newIds);
                              }} />
                              <span className="truncate">{p.project_id}</span>
                              <span className="text-xs text-muted-foreground ml-auto">{p.company}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      {/* Prep/Delivery Hours for selected projects */}
                      {assignProjectIds.length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Split Prep & Delivery Hours</Label>
                          <p className="text-xs text-muted-foreground">The quoted total comes from the master document. Split it into prep and delivery time for the manager.</p>
                          <div className="max-h-52 overflow-auto space-y-3 border border-border rounded-md p-3">
                            {assignProjectIds.map(pid => {
                              const master = masterQuotedHours[pid] || {};
                              const totalQuoted = master.total_quoted || 0;
                              const prepVal = parseFloat(assignProjectHours[pid]?.prep || 0);
                              const delVal = parseFloat(assignProjectHours[pid]?.delivery || 0);
                              const enteredTotal = prepVal + delVal;
                              const diff = enteredTotal - totalQuoted;
                              const hasValues = prepVal > 0 || delVal > 0;
                              
                              return (
                                <div key={pid} className="p-2 rounded-lg bg-muted/30 space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium truncate max-w-[200px]">{pid}</span>
                                    <span className="text-xs text-muted-foreground">
                                      Quoted: <strong className="text-foreground">{totalQuoted > 0 ? `${totalQuoted}h` : "Not set"}</strong>
                                      {master.company && <span className="ml-1">({master.company})</span>}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="flex-1 space-y-1">
                                      <Label className="text-[10px] text-muted-foreground">Prep hours</Label>
                                      <Input type="number" step="0.01" min="0" placeholder="0" className="h-7 text-xs"
                                        value={assignProjectHours[pid]?.prep || ""}
                                        onChange={(e) => setAssignProjectHours(prev => ({ ...prev, [pid]: { ...prev[pid], prep: e.target.value } }))} />
                                    </div>
                                    <div className="flex-1 space-y-1">
                                      <Label className="text-[10px] text-muted-foreground">Delivery hours</Label>
                                      <Input type="number" step="0.01" min="0" placeholder="0" className="h-7 text-xs"
                                        value={assignProjectHours[pid]?.delivery || ""}
                                        onChange={(e) => setAssignProjectHours(prev => ({ ...prev, [pid]: { ...prev[pid], delivery: e.target.value } }))} />
                                    </div>
                                    {hasValues && (
                                      <div className="text-right min-w-[80px]">
                                        <p className="text-xs font-medium tabular-nums">{enteredTotal}h total</p>
                                        {totalQuoted > 0 && diff !== 0 && (
                                          <p className={`text-[10px] font-medium ${diff > 0 ? "text-red-600" : "text-amber-600"}`}>
                                            {diff > 0 ? `+${diff}h over` : `${Math.abs(diff)}h under`}
                                          </p>
                                        )}
                                        {totalQuoted > 0 && diff === 0 && (
                                          <p className="text-[10px] font-medium text-emerald-600">Matches quoted</p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setAssignFormOpen(false)}>Cancel</Button>
                        <Button disabled={!assignManagerId || assignProjectIds.length === 0} onClick={async () => {
                          try {
                            // Build project_hours object only for filled values
                            const projectHours = {};
                            for (const pid of assignProjectIds) {
                              const h = assignProjectHours[pid];
                              if (h?.prep || h?.delivery) {
                                projectHours[pid] = { prep: parseFloat(h.prep || 0), delivery: parseFloat(h.delivery || 0) };
                              }
                            }
                            await axios.post(`${API}/admin/project-assignments`, {
                              manager_user_id: assignManagerId,
                              project_ids: assignProjectIds,
                              project_hours: projectHours,
                              notes: "",
                            });
                            toast.success(`Assigned ${assignProjectIds.length} projects`);
                            setAssignFormOpen(false);
                            setAssignManagerId("");
                            setAssignProjectIds([]);
                            setAssignSearch("");
                            setAssignProjectHours({});
                            fetchData();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || "Failed to create assignment");
                          }
                        }}>Assign Projects</Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {assignments.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p className="font-medium">No assignments yet</p>
                  <p className="text-sm">Click &quot;New Assignment&quot; to assign projects to managers</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {assignments.map(a => (
                    <div key={a.id} className="p-4 rounded-lg border border-border">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                            <span className="text-sm font-semibold text-purple-600">{(a.manager_name || "?").charAt(0)}</span>
                          </div>
                          <div>
                            <p className="font-medium">{a.manager_name}</p>
                            <p className="text-xs text-muted-foreground">{a.manager_email}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" onClick={async () => {
                          try {
                            await axios.delete(`${API}/admin/project-assignments/${a.id}`);
                            toast.success("Assignment removed");
                            fetchData();
                          } catch { toast.error("Failed to remove"); }
                        }}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(a.project_ids || []).map(pid => (
                          <Badge key={pid} variant="secondary" className="text-xs">{pid}</Badge>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">Assigned: {new Date(a.assigned_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Leaders Tab */}
        <TabsContent value="team-leaders" className="space-y-6">
          <TeamLeadersPanel />
        </TabsContent>


        {/* Users Tab */}
        <TabsContent value="users" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-semibold">User Management</CardTitle>
                  <CardDescription>Add, edit, or remove users</CardDescription>
                </div>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="gap-2"><UserPlus className="w-4 h-4" /> Add User</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New User</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Full Name</Label>
                        <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} placeholder="John Smith" />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="john@company.com" />
                      </div>
                      <div className="space-y-2">
                        <Label>Temporary Password (optional - auto-generated if empty)</Label>
                        <Input type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="Leave blank to auto-generate" />
                      </div>
                      <div className="space-y-2">
                        <Label>Role</Label>
                        <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="employee">Employee</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="director">Director</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button variant="outline">Cancel</Button>
                      </DialogClose>
                      <Button onClick={handleCreateUser}>Create & Get Invite</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Invite Sent Dialog */}
                <Dialog open={!!inviteDetails} onOpenChange={(open) => { if (!open) setInviteDetails(null); }}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-emerald-600">
                        <CheckCircle2 className="w-5 h-5" /> User Created
                      </DialogTitle>
                    </DialogHeader>
                    {inviteDetails && (
                      <div className="space-y-4 py-4">
                        {inviteDetails.emailSent ? (
                          <div className="text-center space-y-3">
                            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                              <Mail className="w-8 h-8 text-emerald-600" />
                            </div>
                            <div>
                              <p className="text-lg font-semibold">Invite Sent!</p>
                              <p className="text-sm text-muted-foreground mt-1">Login credentials have been emailed to:</p>
                              <p className="text-base font-medium mt-1">{inviteDetails.email}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground space-y-1">
                              <p><strong>Name:</strong> {inviteDetails.name}</p>
                              <p><strong>Role:</strong> {inviteDetails.role}</p>
                              <p>The email contains their temporary password, login URL, and instructions to set up 2FA.</p>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center space-y-3">
                            <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                              <AlertTriangle className="w-8 h-8 text-amber-600" />
                            </div>
                            <div>
                              <p className="text-lg font-semibold">User Created</p>
                              <p className="text-sm text-muted-foreground mt-1">Email invite could not be sent. Please share credentials manually.</p>
                              <p className="text-base font-medium mt-1">{inviteDetails.email}</p>
                            </div>
                          </div>
                        )}
                        <div className="flex justify-center">
                          <Button onClick={() => setInviteDetails(null)}>Done</Button>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <Input
                  placeholder="Search users by name or email"
                  className="max-w-xs"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />
                {selectedUserIds.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 text-red-600 hover:text-red-700"
                    onClick={async () => {
                      // Bulk delete without browser confirm (sandbox blocks window.confirm)
                      for (const id of selectedUserIds) {
                        await handleDeleteUser(id);
                      }
                      setSelectedUserIds([]);
                    }}
                  >
                    <Trash2 className="w-4 h-4" /> Delete selected
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                {users
                  .filter((u) => {
                    if (!userSearch.trim()) return true;
                    const term = userSearch.toLowerCase();
                    return (
                      u.name?.toLowerCase().includes(term) ||
                      u.email?.toLowerCase().includes(term)
                    );
                  })
                  .map((u) => {
                    const checked = selectedUserIds.includes(u.id);
                    const isEditing = editingUser?.id === u.id;
                    return (
                      <div key={u.id} className="flex items-center justify-between p-4 rounded-lg border border-border" data-testid={`user-row-${u.id}`}>
                        <div className="flex items-center gap-4">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-border"
                            checked={checked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUserIds((prev) => [...prev, u.id]);
                              } else {
                                setSelectedUserIds((prev) => prev.filter((id) => id !== u.id));
                              }
                            }}
                            disabled={u.id === user?.id}
                          />
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                            <span className="text-sm font-semibold text-primary">{u.name?.charAt(0) || "U"}</span>
                          </div>
                          <div>
                            {isEditing ? (
                              <div className="flex items-center gap-2 flex-wrap">
                                <Input 
                                  className="h-7 w-32 text-sm"
                                  value={editingUser.name} 
                                  onChange={(e) => setEditingUser(prev => ({ ...prev, name: e.target.value }))}
                                  placeholder="Name"
                                  data-testid="edit-user-name"
                                />
                                <Input 
                                  className="h-7 w-48 text-sm"
                                  value={editingUser.email} 
                                  onChange={(e) => setEditingUser(prev => ({ ...prev, email: e.target.value }))}
                                  placeholder="Email"
                                  data-testid="edit-user-email"
                                />
                                <Button size="sm" className="h-7 text-xs" data-testid="save-user-btn" onClick={async () => {
                                  try {
                                    await axios.put(`${API}/admin/users/${u.id}`, { name: editingUser.name, email: editingUser.email });
                                    toast.success("User updated successfully");
                                    setEditingUser(null);
                                    // Refresh users list
                                    const res = await axios.get(`${API}/admin/users`);
                                    setUsers(res.data?.items || res.data || []);
                                  } catch (e) { toast.error(e.response?.data?.detail || "Failed to update user"); }
                                }}>Save</Button>
                                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setEditingUser(null)}>Cancel</Button>
                              </div>
                            ) : (
                              <>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{u.name}</span>
                              {u.role === "admin" && (
                                <Badge variant="secondary" className="bg-amber-100 text-amber-800">Admin</Badge>
                              )}
                              {u.role === "director" && (
                                <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">Director</Badge>
                              )}
                              {u.role === "manager" && (
                                <Badge variant="secondary" className="bg-purple-100 text-purple-800">Manager</Badge>
                              )}
                              {(u.role === "employee" || u.role === "user") && (
                                <Badge variant="secondary" className="bg-blue-100 text-blue-800">Employee</Badge>
                              )}
                              {!u.is_active && (
                                <Badge variant="destructive">Disabled</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{u.email}</p>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setEditingUser({ id: u.id, name: u.name || "", email: u.email || "" })}
                            disabled={isEditing}
                            data-testid={`edit-user-btn-${u.id}`}
                          >
                            Edit
                          </Button>
                          <Select 
                            value={u.role} 
                            onValueChange={(v) => handleUpdateUser(u.id, { role: v })}
                            disabled={u.id === user?.id}
                          >
                            <SelectTrigger className="w-24">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="employee">Employee</SelectItem>
                              <SelectItem value="manager">Manager</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="director">Director</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={async () => {
                              const newPw = `Reset${Date.now().toString(36)}!Aa`;
                              try {
                                const res = await axios.put(`${API}/admin/users/${u.id}/password`, { new_password: newPw });
                                const emailSent = res.data?.email_sent;
                                toast.success(
                                  emailSent 
                                    ? `Password reset. Email sent to ${u.email} with new credentials.`
                                    : `Password reset for ${u.name}. New temp password: ${newPw}`,
                                  { duration: 15000 }
                                );
                              } catch (e) { toast.error("Failed to reset password"); }
                            }}
                            disabled={u.id === user?.id}
                          >
                            Reset PW
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/admin/users/${u.id}/reset-2fa`);
                                toast.success(`2FA reset for ${u.name}. They will set up a new code on next login.`);
                              } catch (e) { toast.error("Failed to reset 2FA"); }
                            }}
                            disabled={u.id === user?.id}
                            data-testid={`reset-2fa-btn-${u.id}`}
                          >
                            Reset 2FA
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleUpdateUser(u.id, { is_active: !u.is_active })}
                            disabled={u.id === user?.id}
                          >
                            {u.is_active ? "Disable" : "Enable"}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteUser(u.id)}
                            disabled={u.id === user?.id}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Visibility Defaults Tab */}
        <TabsContent value="visibility" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <EyeOff className="w-4 h-4" /> Visibility Defaults
              </CardTitle>
              <CardDescription>
                Personnel and projects selected here are excluded from KPI totals and dashboard summaries by default.
                They remain visible if a user manually selects them in filters. Use this to keep non-fee-earning staff and
                internal/admin projects out of the headline numbers.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-wrap items-center gap-3">
                <Badge variant="secondary">{(visDefaults.excluded_personnel || []).length} personnel excluded</Badge>
                <Badge variant="secondary">{(visDefaults.excluded_project_ids || []).length} projects excluded</Badge>
                <Button onClick={handleSaveVisibilityDefaults} disabled={visSaving} className="gap-2 ml-auto">
                  <Save className="w-4 h-4" /> {visSaving ? "Saving..." : "Save Defaults"}
                </Button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Personnel exclusions */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">Excluded Personnel</Label>
                    <span className="text-xs text-muted-foreground">
                      {(visDefaults.all_personnel || []).length} total
                    </span>
                  </div>
                  <Input
                    placeholder="Search personnel..."
                    value={visPersonnelSearch}
                    onChange={(e) => setVisPersonnelSearch(e.target.value)}
                    className="h-9"
                  />
                  <div className="border rounded-md max-h-96 overflow-y-auto divide-y">
                    {(visDefaults.all_personnel || [])
                      .filter((p) => !visPersonnelSearch || p.toLowerCase().includes(visPersonnelSearch.toLowerCase()))
                      .map((p) => {
                        const checked = (visDefaults.excluded_personnel || []).includes(p);
                        return (
                          <label
                            key={p}
                            className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => togglePersonnelExclusion(p)}
                              className="w-4 h-4"
                            />
                            <span className={`text-sm ${checked ? "line-through text-muted-foreground" : ""}`}>{p}</span>
                          </label>
                        );
                      })}
                    {(visDefaults.all_personnel || []).length === 0 && (
                      <div className="p-4 text-center text-sm text-muted-foreground">No personnel found</div>
                    )}
                  </div>
                </div>

                {/* Project exclusions */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">Excluded Projects</Label>
                    <span className="text-xs text-muted-foreground">
                      {(visDefaults.all_project_ids || []).length} total
                    </span>
                  </div>
                  <Input
                    placeholder="Search projects..."
                    value={visProjectSearch}
                    onChange={(e) => setVisProjectSearch(e.target.value)}
                    className="h-9"
                  />
                  <div className="border rounded-md max-h-96 overflow-y-auto divide-y">
                    {(visDefaults.all_project_ids || [])
                      .filter((p) => !visProjectSearch || p.toLowerCase().includes(visProjectSearch.toLowerCase()))
                      .map((p) => {
                        const checked = (visDefaults.excluded_project_ids || []).includes(p);
                        return (
                          <label
                            key={p}
                            className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleProjectExclusion(p)}
                              className="w-4 h-4"
                            />
                            <span className={`text-sm font-mono ${checked ? "line-through text-muted-foreground" : ""}`}>{p}</span>
                          </label>
                        );
                      })}
                    {(visDefaults.all_project_ids || []).length === 0 && (
                      <div className="p-4 text-center text-sm text-muted-foreground">No projects found</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="text-xs text-muted-foreground border-t pt-3">
                Note: Excluded items are filtered from <strong>Profitability</strong>, <strong>Company Summary</strong>, and KPI totals
                by default. They still appear in detailed reports if a user manually selects them.
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========== SESSIONS TAB ========== */}
        <TabsContent value="sessions" className="space-y-6">
          <SessionManagementPanel />
        </TabsContent>

        {/* ========== AUDIT TRAIL TAB ========== */}
        <TabsContent value="audit-trail" className="space-y-6">
          <AuditTrailPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
